import { createRoute } from "@tanstack/react-router";
import { useForm } from "@tanstack/react-form";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { AdminLayoutRoute } from "./admin-layout";
import { SettingsLayoutRoute } from "./settings-layout";
import { useSession } from "~webui/lib/session";
import { Dialog } from "~webui/components/Dialog";
import { Switch } from "~webui/components/Switch";
import type { ApiKeyRecord } from "~webui/lib/api";

function Json({ value }: { value: unknown }) {
  return (
    <pre className="muted">
      {typeof value === "string" ? value : JSON.stringify(value, null, 2)}
    </pre>
  );
}

export const UsersRoute = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/users",
  component: UsersPage,
});
function UsersPage() {
  const { api, org } = useSession();
  const [out, setOut] = useState<unknown>(
    "Provision an email, then grant access. The user signs in through Keyname.",
  );
  const [modal, setModal] = useState<"create" | null>(null);
  const form = useForm({
    defaultValues: { email: "" },
    onSubmit: async ({ value }) => {
      setOut(await api.createUser({ org, ...value }));
      setModal(null);
    },
  });
  return (
    <>
      <div className="card">
        <h3>User administration</h3>
        <p className="muted">
          Provision users by their verified Keyname email, then assign repo/runtime grants.
          Credentials, MFA, passkeys, and recovery remain on keyname.dev.
        </p>
        <div className="actions">
          <button onClick={() => setModal("create")}>Provision user</button>
        </div>
        <Json value={out} />
      </div>
      {modal && (
        <Dialog title="Provision Keyname user" onClose={() => setModal(null)}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void form.handleSubmit().catch(setOut);
            }}
          >
            <form.Field name="email">
              {(f) => (
                <>
                  <label>Email</label>
                  <input value={f.state.value} onChange={(e) => f.handleChange(e.target.value)} />
                </>
              )}
            </form.Field>
            <p className="muted text-sm">
              The email must match a verified Keyname identity. No dot.locker password is created.
            </p>
            <button>Provision user</button>
          </form>
        </Dialog>
      )}
    </>
  );
}

export const TokensRoute = createRoute({
  getParentRoute: () => SettingsLayoutRoute,
  path: "/settings/api-keys",
  component: TokensPage,
});
function TokensPage() {
  const { api, org } = useSession();
  const [keys, setKeys] = useState<ApiKeyRecord[]>([]);
  const [sessions, setSessions] = useState<ApiKeyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modal, setModal] = useState<"create" | "created" | "edit" | "revoke" | null>(null);
  const [selected, setSelected] = useState<ApiKeyRecord | null>(null);
  const [label, setLabel] = useState("local development");
  const [repo, setRepo] = useState("portal");
  const [runtime, setRuntime] = useState("");
  const [allRepos, setAllRepos] = useState(false);
  const [readAccess, setReadAccess] = useState(true);
  const [writeAccess, setWriteAccess] = useState(true);
  const [expires, setExpires] = useState("2592000");
  const [plaintext, setPlaintext] = useState("");
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await api.tokens();
      setKeys(result.tokens);
      setSessions(result.sessions ?? []);
    } catch (reason) {
      setError(`Could not load API keys: ${JSON.stringify(reason)}`);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setLabel("local development");
    setRepo("portal");
    setRuntime("");
    setAllRepos(false);
    setReadAccess(true);
    setWriteAccess(true);
    setExpires("2592000");
    setPlaintext("");
    setCopied(false);
    setError("");
    setModal("create");
  };

  const createKey = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const path = allRepos
        ? `${org}/**`
        : [org, repo.trim(), runtime.trim() || undefined, "**"].filter(Boolean).join("/");
      const scopes = permissionScopes(path, readAccess, writeAccess);
      if (!scopes.length) throw new Error("Select at least one permission");
      const result = await api.createToken({
        label: label.trim(),
        scopes,
        ...(expires ? { expiresSeconds: Number(expires) } : {}),
      });
      setPlaintext(result.token);
      await load();
      setModal("created");
    } catch (reason) {
      setError(`Could not create API key: ${JSON.stringify(reason)}`);
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (key: ApiKeyRecord) => {
    const path = key.scopes[0]?.path ?? `${org}/**`;
    const parts = path.split("/");
    const broad = path === `${org}/**`;
    setSelected(key);
    setAllRepos(broad);
    setRepo(broad ? "" : (parts[1] ?? ""));
    setRuntime(!broad && parts.length > 3 ? (parts[2] ?? "") : "");
    setReadAccess(key.scopes.some((scope) => scope.access === "read"));
    setWriteAccess(key.scopes.some((scope) => scope.access === "write"));
    setError("");
    setModal("edit");
  };

  const savePermissions = async (event: FormEvent) => {
    event.preventDefault();
    if (!selected) return;
    setSubmitting(true);
    setError("");
    try {
      const path = allRepos
        ? `${org}/**`
        : [org, repo.trim(), runtime.trim() || undefined, "**"].filter(Boolean).join("/");
      const scopes = permissionScopes(path, readAccess, writeAccess);
      if (!scopes.length) throw new Error("Select at least one permission");
      await api.updateToken(selected.id, scopes);
      await load();
      setModal(null);
      setSelected(null);
    } catch (reason) {
      setError(`Could not update API key: ${JSON.stringify(reason)}`);
    } finally {
      setSubmitting(false);
    }
  };

  const revokeKey = async () => {
    if (!selected) return;
    setSubmitting(true);
    setError("");
    try {
      await api.revokeToken(selected.id);
      await load();
      setModal(null);
      setSelected(null);
    } catch (reason) {
      setError(`Could not revoke API key: ${JSON.stringify(reason)}`);
    } finally {
      setSubmitting(false);
    }
  };

  const statusFor = (key: ApiKeyRecord) => {
    if (key.revokedAt) return "Revoked";
    if (key.expiresAt && key.expiresAt <= Date.now()) return "Expired";
    return "Active";
  };

  return (
    <>
      <section className="card p-0">
        <header className="flex flex-wrap items-center justify-between gap-4 border-b border-[var(--pl-line)] px-5 py-4">
          <div>
            <h3 className="mb-0">API keys</h3>
            <p className="mb-0 mt-1 text-xs text-[var(--pl-muted)]">
              {loading ? "Loading API keys…" : `${keys.length} keys in ${org}`}
            </p>
          </div>
          <button onClick={openCreate}>
            <span className="text-lg leading-none" aria-hidden>
              +
            </span>{" "}
            Create API key
          </button>
        </header>
        <div className="border-b border-[var(--pl-line)] bg-[var(--pl-surface)] px-5 py-3 text-xs text-[var(--pl-muted)]">
          Local dot.locker configuration uses an API key as its only authentication credential. Each
          key grants explicit path and access rights.
        </div>
        {error && !modal && (
          <div className="mx-5 mt-4 rounded-[var(--pl-radius-xs)] border border-[var(--pl-danger)]/40 bg-[var(--pl-danger)]/5 px-3 py-2 text-xs text-[var(--pl-danger)]">
            {error}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="mt-0 min-w-[880px] rounded-none border-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Owner</th>
                <th>Key ID</th>
                <th>Grant scope</th>
                <th>Access rights</th>
                <th>Status</th>
                <th>Created</th>
                <th className="w-40 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.length ? (
                keys.map((key) => {
                  const status = statusFor(key);
                  return (
                    <tr key={key.id}>
                      <td>
                        <b className="text-[var(--pl-text)]">{key.label || "Unnamed key"}</b>
                      </td>
                      <td className="text-xs text-[var(--pl-muted)]">
                        {key.userEmail ?? "Service"}
                      </td>
                      <td>
                        <code>{key.id}</code>
                      </td>
                      <td>
                        {key.scopes.map((scope) => (
                          <code key={`${scope.path}:${scope.access}`} className="block">
                            {scope.path}
                          </code>
                        ))}
                      </td>
                      <td>
                        {key.scopes.map((scope) => (
                          <span key={`${scope.path}:${scope.access}`} className="pill capitalize">
                            {scope.access}
                          </span>
                        ))}
                      </td>
                      <td>
                        <span
                          className={`pill ${status === "Active" ? "text-[var(--pl-success)]" : "text-[var(--pl-subtle)]"}`}
                        >
                          {status}
                        </span>
                      </td>
                      <td>{new Date(key.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div className="flex justify-end gap-2">
                          <button
                            className="secondary inline"
                            disabled={status !== "Active"}
                            onClick={() => openEdit(key)}
                          >
                            Edit
                          </button>
                          <button
                            className="secondary inline"
                            disabled={status === "Revoked"}
                            onClick={() => {
                              setSelected(key);
                              setError("");
                              setModal("revoke");
                            }}
                          >
                            Revoke
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-[var(--pl-muted)]">
                    {loading ? "Loading API keys…" : "No API keys found."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card p-0">
        <header className="border-b border-[var(--pl-line)] px-5 py-4">
          <h3 className="mb-0">Active sessions</h3>
          <p className="mb-0 mt-1 text-xs text-[var(--pl-muted)]">
            Keyname browser sessions visible to you in {org}. Revoke a session to require sign-in
            again.
          </p>
        </header>
        <div className="overflow-x-auto">
          <table className="mt-0 min-w-[760px] rounded-none border-0">
            <thead>
              <tr>
                <th>Identity</th>
                <th>Session</th>
                <th>Created</th>
                <th>Expires</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((session) => (
                <tr key={session.id}>
                  <td>{session.userEmail ?? "Unknown"}</td>
                  <td>
                    <code>{session.id}</code>
                  </td>
                  <td>{new Date(session.createdAt).toLocaleString()}</td>
                  <td>{session.expiresAt ? new Date(session.expiresAt).toLocaleString() : "—"}</td>
                  <td>
                    <span className="pill">{statusFor(session)}</span>
                  </td>
                  <td>
                    <button
                      className="secondary inline"
                      disabled={statusFor(session) !== "Active"}
                      onClick={() => {
                        setSelected(session);
                        setError("");
                        setModal("revoke");
                      }}
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
              {!sessions.length && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-[var(--pl-muted)]">
                    No active sessions found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {modal === "create" && (
        <Dialog title="Create API key" onClose={() => setModal(null)}>
          <form onSubmit={(event) => void createKey(event)}>
            <p className="mt-0 text-sm text-[var(--pl-muted)]">
              Create an API key and grant only the access rights required by this local
              configuration.
            </p>
            <label htmlFor="key-label">Name</label>
            <input
              id="key-label"
              autoFocus
              required
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="local development"
            />
            <div className="mt-4">
              <Switch
                checked={allRepos}
                onCheckedChange={setAllRepos}
                label="Organization-wide key"
                description={`Grant access across every repository and runtime in ${org}. Broad write requires an organization administrator.`}
              />
            </div>
            <div className="row">
              <div>
                <label htmlFor="key-repo">Repository</label>
                <input
                  id="key-repo"
                  required={!allRepos}
                  disabled={allRepos}
                  value={repo}
                  onChange={(event) => setRepo(event.target.value)}
                />
              </div>
              <div>
                <label htmlFor="key-runtime">
                  Runtime <span className="font-normal text-[var(--pl-subtle)]">(optional)</span>
                </label>
                <input
                  id="key-runtime"
                  disabled={allRepos}
                  value={runtime}
                  onChange={(event) => setRuntime(event.target.value)}
                  placeholder="All runtimes"
                />
              </div>
            </div>
            <label>Access rights</label>
            <div className="grid gap-2">
              <Switch
                checked={readAccess}
                onCheckedChange={setReadAccess}
                label="Read"
                description="Download files, resolve runtimes, and inspect version history."
              />
              <Switch
                checked={writeAccess}
                onCheckedChange={setWriteAccess}
                label="Write"
                description="Upload files, publish runtime versions, and update heads."
              />
            </div>
            <label htmlFor="key-expiry">Expiration</label>
            <select
              id="key-expiry"
              value={expires}
              onChange={(event) => setExpires(event.target.value)}
            >
              <option value="86400">1 day</option>
              <option value="604800">7 days</option>
              <option value="2592000">30 days</option>
              <option value="7776000">90 days</option>
              <option value="">Never</option>
            </select>
            <div className="mt-4 rounded-[var(--pl-radius-xs)] border border-[var(--pl-line)] bg-[var(--pl-surface)] p-3 text-xs text-[var(--pl-muted)]">
              Grant:{" "}
              <code>
                {allRepos
                  ? `${org}/**`
                  : [org, repo || "<repository>", runtime || undefined, "**"]
                      .filter(Boolean)
                      .join("/")}
              </code>{" "}
              ·{" "}
              <b className="text-[var(--pl-text)]">
                {[readAccess && "read", writeAccess && "write"].filter(Boolean).join(" + ") ||
                  "no permissions selected"}
              </b>
            </div>
            {error && <p className="err mt-3 text-xs">{error}</p>}
            <div className="actions justify-end border-t border-[var(--pl-line)] pt-4">
              <button type="button" className="secondary" onClick={() => setModal(null)}>
                Cancel
              </button>
              <button type="submit" disabled={submitting || (!readAccess && !writeAccess)}>
                {submitting ? "Creating…" : "Create API key"}
              </button>
            </div>
          </form>
        </Dialog>
      )}

      {modal === "edit" && selected && (
        <Dialog title="Edit API key permissions" onClose={() => setModal(null)}>
          <form onSubmit={(event) => void savePermissions(event)}>
            <p className="mt-0 text-sm text-[var(--pl-muted)]">
              Update the path and access rights for <b>{selected.label || selected.id}</b>. Changes
              apply immediately without rotating the key.
            </p>
            <Switch
              checked={allRepos}
              onCheckedChange={setAllRepos}
              label="Organization-wide key"
              description={`Grant access across every repository and runtime in ${org}. Broad write requires an organization administrator.`}
            />
            <div className="row mt-3">
              <div>
                <label htmlFor="edit-key-repo">Repository</label>
                <input
                  id="edit-key-repo"
                  required={!allRepos}
                  disabled={allRepos}
                  value={repo}
                  onChange={(event) => setRepo(event.target.value)}
                />
              </div>
              <div>
                <label htmlFor="edit-key-runtime">
                  Runtime <span className="font-normal text-[var(--pl-subtle)]">(optional)</span>
                </label>
                <input
                  id="edit-key-runtime"
                  disabled={allRepos}
                  value={runtime}
                  onChange={(event) => setRuntime(event.target.value)}
                  placeholder="All runtimes"
                />
              </div>
            </div>
            <label>Access rights</label>
            <div className="grid gap-2">
              <Switch
                checked={readAccess}
                onCheckedChange={setReadAccess}
                label="Read"
                description="Download files, resolve runtimes, and inspect version history."
              />
              <Switch
                checked={writeAccess}
                onCheckedChange={setWriteAccess}
                label="Write"
                description="Upload files, publish runtime versions, and update heads."
              />
            </div>
            <div className="mt-4 rounded-[var(--pl-radius-xs)] border border-[var(--pl-line)] bg-[var(--pl-surface)] p-3 text-xs text-[var(--pl-muted)]">
              Grant:{" "}
              <code>
                {allRepos
                  ? `${org}/**`
                  : [org, repo || "<repository>", runtime || undefined, "**"]
                      .filter(Boolean)
                      .join("/")}
              </code>{" "}
              ·{" "}
              <b className="text-[var(--pl-text)]">
                {[readAccess && "read", writeAccess && "write"].filter(Boolean).join(" + ") ||
                  "no permissions selected"}
              </b>
            </div>
            {error && <p className="err mt-3 text-xs">{error}</p>}
            <div className="actions justify-end border-t border-[var(--pl-line)] pt-4">
              <button type="button" className="secondary" onClick={() => setModal(null)}>
                Cancel
              </button>
              <button type="submit" disabled={submitting || (!readAccess && !writeAccess)}>
                {submitting ? "Saving…" : "Save permissions"}
              </button>
            </div>
          </form>
        </Dialog>
      )}

      {modal === "created" && (
        <Dialog title="API key created" onClose={() => setModal(null)}>
          <div className="rounded-[var(--pl-radius-xs)] border border-[var(--pl-success)]/40 bg-[var(--pl-success)]/5 p-3 text-sm text-[var(--pl-muted)]">
            Copy this API key now. It will not be shown again.
          </div>
          <label htmlFor="created-key">API key</label>
          <textarea
            id="created-key"
            readOnly
            rows={3}
            className="w-full resize-none rounded-[var(--pl-radius-xs)] border border-[var(--pl-line)] bg-[var(--pl-bg)] p-3 font-mono text-xs text-[var(--pl-text)] outline-none"
            value={plaintext}
          />
          <p className="text-xs text-[var(--pl-muted)]">
            Use it locally as <code>DOTLOCKER_TOKEN</code>. No username or password is required.
          </p>
          <div className="actions justify-end border-t border-[var(--pl-line)] pt-4">
            <button
              className="secondary"
              onClick={() =>
                void navigator.clipboard.writeText(plaintext).then(() => setCopied(true))
              }
            >
              {copied ? "Copied" : "Copy API key"}
            </button>
            <button onClick={() => setModal(null)}>Done</button>
          </div>
        </Dialog>
      )}

      {modal === "revoke" && selected && (
        <Dialog
          title={
            sessions.some((session) => session.id === selected.id)
              ? "Revoke session"
              : "Revoke API key"
          }
          onClose={() => setModal(null)}
        >
          <p className="mt-0 text-sm text-[var(--pl-muted)]">
            Revoke{" "}
            <b className="text-[var(--pl-text)]">
              {selected.label || selected.userEmail || selected.id}
            </b>
            ?{" "}
            {sessions.some((session) => session.id === selected.id)
              ? "That browser will need to sign in again."
              : "Local configurations using this key will immediately lose access."}
          </p>
          {error && <p className="err mt-3 text-xs">{error}</p>}
          <div className="actions justify-end border-t border-[var(--pl-line)] pt-4">
            <button className="secondary" onClick={() => setModal(null)}>
              Cancel
            </button>
            <button className="danger" disabled={submitting} onClick={() => void revokeKey()}>
              {submitting ? "Revoking…" : "Revoke API key"}
            </button>
          </div>
        </Dialog>
      )}
    </>
  );
}

export const AuditRoute = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/audit",
  component: AuditPage,
});
function AuditPage() {
  const { api, org } = useSession();
  const [limit, setLimit] = useState(100);
  const [out, setOut] = useState<unknown>("No audit loaded.");
  return (
    <div className="card">
      <h3>Audit events</h3>
      <p className="muted">
        Audit events show authentication, file access, grant, token, and warning activity for this
        organization.
      </p>
      <div className="row">
        <div>
          <label>Limit</label>
          <input value={limit} onChange={(e) => setLimit(Number(e.target.value) || 100)} />
        </div>
        <div>
          <button onClick={() => void api.audit(org, limit).then(setOut).catch(setOut)}>
            Load audit
          </button>
        </div>
      </div>
      <Json value={out} />
    </div>
  );
}

export const SettingsRoute = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/legacy-settings",
  component: SettingsPage,
});
function permissionScopes(path: string, read: boolean, write: boolean) {
  return [
    ...(read ? [{ path, access: "read" as const }] : []),
    ...(write ? [{ path, access: "write" as const }] : []),
  ];
}

function SettingsPage() {
  const s = useSession();
  const [org, setOrg] = useState(s.org);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const saveOrganization = async () => {
    if (org === s.org) return;
    setSaving(true);
    setError("");
    try {
      const next = await s.api.switchOrg(org);
      s.setMe(next);
      s.setOrg(next.token.org);
    } catch (reason) {
      setError(`Could not switch organization: ${JSON.stringify(reason)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid cols-2">
      <div className="card">
        <h3>Connection</h3>
        <p className="muted">
          dot.locker uses the authenticated same-origin connection. Organizations are limited to
          your current grants.
        </p>
        <label>API endpoint</label>
        <div className="rounded-[var(--pl-radius-xs)] border border-[var(--pl-line)] bg-[var(--pl-surface)] px-3 py-2.5 text-sm text-[var(--pl-muted)]">
          {location.origin}
        </div>
        <label htmlFor="settings-org">Organization</label>
        <select id="settings-org" value={org} onChange={(event) => setOrg(event.target.value)}>
          {(s.me?.orgs.length ? s.me.orgs : [s.org]).map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        {error && <p className="err mt-3 text-xs">{error}</p>}
        <div className="actions">
          <button disabled={saving || org === s.org} onClick={() => void saveOrganization()}>
            {saving ? "Switching…" : "Switch organization"}
          </button>
        </div>
      </div>
      <div className="card">
        <h3>Session</h3>
        <p className="muted">
          Signed in as{" "}
          <b className="text-[var(--pl-text)]">
            {s.me?.identityEmail ?? s.me?.token.userEmail ?? "Loading…"}
          </b>
          . Signing out also closes the active Keyname session.
        </p>
        <button
          className="danger"
          onClick={async () => {
            await Promise.allSettled([s.api.logout(), window.Keyname.signOut()]);
            s.logout();
            location.assign("/login");
          }}
        >
          Log out of dot.locker
        </button>
      </div>
    </div>
  );
}
